import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import { useAnalytics } from "../hooks/useAnalytics";
import { motion } from "framer-motion";
import '../styles/contact.css';
import { ShineBorder } from '../components/ui/shine-border';

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.4 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  const formVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login({ username, password });
      analytics.trackLogin("email");
      analytics.trackEvent("login_success", {
        category: "authentication",
        username: username,
      });
      setLoginSuccess(true);
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1500);
    } catch (err) {
      analytics.trackEvent("login_failed", {
        category: "authentication",
        error: "invalid_credentials",
      });
      console.error("Erro no login:", err);
      const errorMessage =
        err.response?.data?.error?.message ||
        (typeof err.response?.data?.error === 'string' ? err.response.data.error : null) ||
        err.response?.data?.message ||
        err.data?.error?.message ||
        (typeof err.data?.error === 'string' ? err.data.error : null) ||
        err.data?.message ||
        err.message ||
        "Credenciais inválidas. Tente novamente.";
      const errorCode = err.response?.data?.code || err.code;

      if (errorCode === "USER_INACTIVE" || errorMessage === "Usuário inativo") {
        setShowInactiveModal(true);
        setError("");
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="bg-background text-on-surface font-body min-h-screen flex flex-col items-center relative overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        '--color-background': '#131315',
        '--color-surface': '#131315',
        '--color-surface-container': '#201f21',
        '--color-surface-container-low': '#1b1b1d',
        '--color-surface-container-high': '#2a2a2c',
        '--color-surface-container-highest': '#353437',
        '--color-surface-container-lowest': '#0e0e10',
        '--color-on-surface': '#e5e1e4',
        '--color-on-surface-variant': '#bbcbb8',
        '--color-primary': '#51e1a5',
        '--color-primary-container': '#2ac48b',
        '--color-on-primary': '#003824',
        '--color-tertiary': '#d1c0ff',
        '--color-outline-variant': '#3c4a3c',
      }}
    >
      {/* Decorative orbs */}
      <div className="absolute top-1/4 right-10 w-96 h-96 bg-primary opacity-5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-10 w-64 h-64 bg-tertiary opacity-5 rounded-full blur-[100px] pointer-events-none" />

      {/* ConfirmModal for inactive account */}
      <ConfirmModal
        isOpen={showInactiveModal}
        onClose={() => setShowInactiveModal(false)}
        onConfirm={() => setShowInactiveModal(false)}
        title="Usuário inativado"
        message="Entre em contato com a administração"
        type="danger"
        confirmText="Entendi"
        showCancel={false}
      />

      {/* Top nav */}
      <nav className="bg-transparent w-full absolute top-0 left-0 flex justify-between items-center px-8 py-6 z-10">
        <div className="text-2xl font-black text-white tracking-tighter font-headline">PapIA</div>
      </nav>

      {/* Main content */}
      <main className="flex-grow flex flex-col items-center justify-center w-full max-w-7xl px-6 py-24">
        <div className="relative w-full max-w-md">

          {/* Success overlay — fades in on top, no exit needed */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: loginSuccess ? 1 : 0,
              pointerEvents: loginSuccess ? "auto" : "none",
              transition: "opacity 0.4s ease",
              zIndex: 20,
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #51e1a5 0%, #2ac48b 100%)",
                padding: "1px",
                borderRadius: "0.5rem",
              }}
              className="shadow-2xl"
            >
              <div className="bg-surface-container rounded-[calc(0.5rem-1px)] p-10 text-center">
                <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary-container flex items-center justify-center mb-6 shadow-lg">
                  <svg className="h-10 w-10 text-on-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-on-surface mb-3">Login Realizado!</h2>
                <p className="text-on-surface-variant">Redirecionando para o dashboard...</p>
                <div className="mt-6 flex justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
            </div>
          </div>

          {/* Login form — fades out when success */}
          <motion.div
            variants={formVariants}
            initial="hidden"
            animate="visible"
            style={{
              opacity: loginSuccess ? 0 : 1,
              transition: "opacity 0.3s ease",
              zIndex: 10,
              position: "relative",
            }}
          >
            {/* Header section */}
            <motion.div className="mb-12 text-center md:text-left" variants={itemVariants}>
              <h1 className="font-headline text-5xl font-extrabold tracking-tighter text-on-surface mb-4">
                Bem-vindo de volta
              </h1>
              <p className="font-body text-on-surface-variant max-w-xs">
                Faça login na sua conta
              </p>
            </motion.div>

            {/* Card with ShineBorder */}
            <motion.div
              className="relative overflow-hidden rounded-lg bg-surface-container shadow-2xl"
              variants={itemVariants}
            >
              <ShineBorder
                borderWidth={1.5}
                duration={8}
                shineColor={['#51e1a5', '#d1c0ff', '#67e8f9']}
              />
              <div className="p-5 sm:p-8 md:p-10">
                <form onSubmit={handleSubmit} className="space-y-6">

                  {/* Error display */}
                  {error && (
                    <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  {/* Username input */}
                  <div className="space-y-2">
                    <label
                      htmlFor="username"
                      className="block font-label text-sm font-semibold text-on-surface-variant ml-1"
                    >
                      Usuário
                    </label>
                    <div className="uiverse-control">
                      <input
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="username"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Digite seu usuário"
                        className="input-alt"
                        style={{ paddingInline: '0.5rem' }}
                      />
                      <span className="input-border-alt" />
                    </div>
                  </div>

                  {/* Password input */}
                  <div className="space-y-2">
                    <label
                      htmlFor="password"
                      className="block font-label text-sm font-semibold text-on-surface-variant ml-1"
                    >
                      Senha
                    </label>
                    <div className="uiverse-control">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="input-alt"
                        style={{ paddingInline: '0.5rem', paddingRight: '2.5rem' }}
                      />
                      <span className="input-border-alt" />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pb-1 flex items-center text-on-surface-variant hover:text-primary transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-[18px] h-[18px]" />
                        ) : (
                          <Eye className="w-[18px] h-[18px]" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 rounded-lg font-bold text-base shadow-lg transition-all flex justify-center items-center gap-2 ${loading
                      ? "bg-surface-container-highest text-on-surface-variant cursor-not-allowed"
                      : "bg-gradient-to-br from-primary to-primary-container text-on-primary hover:brightness-110 active:scale-[0.98]"
                      }`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2.5">
                        <span className="w-4 h-4 border-2 border-on-surface-variant border-t-transparent rounded-full animate-spin inline-block" />
                        Entrando...
                      </span>
                    ) : (
                      <>
                        Acessar Dashboard
                        <ArrowLeft className="w-5 h-5 rotate-180" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Back button below card */}
            <motion.div className="mt-8 flex justify-center" variants={itemVariants}>
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-stone-500 hover:text-primary font-medium text-sm transition-all py-2 px-4 rounded-full hover:bg-surface-container"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
            </motion.div>
          </motion.div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-transparent text-stone-500 text-sm w-full absolute bottom-0 flex justify-center items-center px-8 py-8 max-w-none">
        <div className="opacity-80">© 2025 AyTech. Todos os direitos reservados.</div>
      </footer>
    </motion.div>
  );
};

export default Login;
